import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingBag, ShoppingCart, Plus, Minus, Info, User, Loader2, Search, Sparkles, Package, MessageCircle, Stethoscope, FileDown } from "lucide-react";
import { humanBytes } from "@/lib/imageCompress";
import { useCart } from "./cartContext";
import { useTenant } from "./tenantContext";
import ShopFooter from "./ShopFooter";
import ProductGallery from "./ProductGallery";
import InstallPrompt, { InstallButton } from "./InstallPrompt";

const IMAGE_DISCLAIMER =
  "Image for reference only. The actual product may differ in appearance, packaging, brand or minor specs.";

export default function ShopCatalog() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [brand, setBrand] = useState({});
  const [downloads, setDownloads] = useState([]);
  const [downloadsOpen, setDownloadsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [favourites, setFavourites] = useState([]);
  // Full-screen zoomable gallery when a shopper taps a product photo.
  const [galleryItem, setGalleryItem] = useState(null);
  const { add, remove, items: cart, count } = useCart();
  const { ownerId, slug, status, brand: tenantBrand, healthcareEnabled } = useTenant();

  const load = useCallback(async () => {
    // Wait for the slug resolver before hitting the catalog so multi-tenant
    // URLs load the RIGHT shop, not the default fallback tenant.
    if (status === "loading") return;
    try {
      const params = ownerId ? { owner_id: ownerId } : {};
      const [cat, br, fav, dl] = await Promise.all([
        api.get("/store/catalog", { params }),
        api.get("/store/brand", { params: slug ? { slug } : params }),
        // Favourites — only relevant for signed-in customers; a 401/403
        // shouldn't crash the catalog for guests.
        api.get("/store/frequent-items").catch(() => ({ data: [] })),
        api.get("/store/downloads", { params }).catch(() => ({ data: [] })),
      ]);
      setItems(cat.data || []);
      setBrand(br.data || {});
      setFavourites(fav.data || []);
      setDownloads(dl.data || []);
    } catch (err) {
      toast.error("Could not load catalog");
    } finally {
      setLoading(false);
    }
  }, [ownerId, slug, status]);

  useEffect(() => { load(); }, [load]);

  const shopHomeLink = slug ? `/shop/${slug}` : "/shop";

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (i) => (i.name || "").toLowerCase().includes(s) || (i.group || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  // Group the (search-filtered) items by their `group` field so long
  // catalogs are digestible. Ungrouped items get bucketed under "Other".
  // Groups render in alphabetical order but "Other" is pinned to the end.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const it of filtered) {
      const g = (it.group || "").trim() || "Other";
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g).push(it);
    }
    const entries = Array.from(buckets.entries()).sort(([a], [b]) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
    return entries; // [ [groupName, items[]], ... ]
  }, [filtered]);

  // Which group sections are collapsed. Nothing is collapsed on first
  // paint — expanding all lets the shopper scroll immediately. They can
  // tap group headers to collapse noisy ones.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (g) => setCollapsedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (status === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="border-slate-200 shadow-none max-w-md w-full">
          <CardContent className="p-8 text-center">
            <ShoppingBag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="font-heading text-lg font-semibold">Shop not found</div>
            <p className="text-slate-500 text-sm mt-1">
              The link you followed doesn&apos;t match any shop right now. Double-check the URL or head back to the main storefront.
            </p>
            <Link to="/shop" className="mt-4 inline-block text-[var(--shop-primary,#0055FF)] hover:underline text-sm">Go to /shop →</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandColor = brand.primary_color || "var(--shop-primary,#0055FF)";
  const brandName = brand.shop_name || "DialFlow Shop";

  return (
    <div className="min-h-screen bg-slate-50 shop-font">
      {tenantBrand?.hero_image_url && (
        <div
          className="relative w-full overflow-hidden"
          style={{ aspectRatio: "16/6" }}
          data-testid="shop-hero"
        >
          <img
            src={tenantBrand.hero_image_url}
            alt="hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {tenantBrand.hero_headline && (
            <div className="relative z-10 flex items-end h-full max-w-6xl mx-auto px-6 pb-6">
              <h1
                className="font-heading text-white text-3xl sm:text-5xl font-bold drop-shadow-lg"
                style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
                data-testid="shop-hero-headline"
              >
                {tenantBrand.hero_headline}
              </h1>
            </div>
          )}
          {/* Subtle bottom gradient so text stays readable over any photo. */}
          <div
            className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
            style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)" }}
          />
        </div>
      )}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <Link to={shopHomeLink} className="flex items-center gap-2">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt="" className="w-6 h-6 rounded object-contain" />
          ) : (
            <ShoppingBag className="w-5 h-5" style={{ color: brandColor }} />
          )}
          <span className="font-heading font-bold">{brandName}</span>
        </Link>
        <div className="flex items-center gap-2">
          {healthcareEnabled && slug && (
            <Link
              to={`/care/${slug}`}
              className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100"
              data-testid="shop-care-link"
              title="Book a doctor or lab test"
            >
              <Stethoscope className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Book a visit</span>
            </Link>
          )}
          {downloads.length > 0 && (
            <button
              type="button"
              onClick={() => setDownloadsOpen(true)}
              className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100"
              data-testid="shop-downloads-link"
              title="Downloads"
            >
              <FileDown className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Download</span>
            </button>
          )}
          <InstallButton shopName={tenantBrand?.shop_name || ""} />
          <Link
            to="/shop/chat"
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100"
            data-testid="shop-chat-link"
            title="Community chat"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline text-sm">Chat</span>
          </Link>
          <Link
            to="/shop/orders"
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100"
            data-testid="shop-orders-link"
            title="Order history"
          >
            <Package className="w-5 h-5" />
            <span className="hidden sm:inline text-sm">Orders</span>
          </Link>
          <Link to="/shop/account" className="text-slate-500 hover:text-slate-900" data-testid="shop-account-link">
            <User className="w-5 h-5" />
          </Link>
          <Link
            to="/shop/checkout"
            className="relative inline-flex items-center gap-1 rounded-full text-white px-3 py-1.5 text-sm hover:opacity-90"
            style={{ backgroundColor: brandColor }}
            data-testid="shop-cart-link"
          >
            <ShoppingCart className="w-4 h-4" />
            <span>Cart</span>
            {count > 0 && (
              <span className="ml-1 bg-white rounded-full text-[10px] px-1.5 py-0.5 font-medium" style={{ color: brandColor }}>
                {count}
              </span>
            )}
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold">Products</h1>
            {brand.tagline && <p className="text-xs text-slate-500 italic">{brand.tagline}</p>}
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="shop-search" />
          </div>
        </div>
        <p className="text-amber-800 text-xs flex items-center gap-1 mb-3">
          <Info className="w-3.5 h-3.5" /> {IMAGE_DISCLAIMER}
        </p>

        {/* Your favourites — top items this customer has bought before. */}
        {favourites.length > 0 && !q && (
          <div className="mb-6" data-testid="shop-favourites">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#FF5000]" />
              <h2 className="font-heading font-semibold text-sm uppercase tracking-widest text-slate-700">
                Your favourites
              </h2>
              <span className="mono text-[10px] text-slate-400">buy again in one tap</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {favourites.map((f) => {
                const inCart = cart[f.id];
                return (
                  <Card
                    key={f.id}
                    className="border-slate-200 shadow-none overflow-hidden ring-1 ring-[#FF5000]/30 hover:ring-[#FF5000]/60 transition"
                    data-testid={`shop-fav-${f.id}`}
                  >
                    <div className="flex gap-3 p-2">
                      <button
                        type="button"
                        onClick={() => f.image && setGalleryItem({ images: [f.image], name: f.name })}
                        className="w-16 h-16 shrink-0 bg-slate-100 rounded overflow-hidden hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--shop-primary,#0055FF)]"
                        data-testid={`shop-fav-img-${f.id}`}
                        aria-label={`View photos of ${f.name}`}
                      >
                        {f.image ? <img src={f.image} alt={f.name} className="w-full h-full object-cover" /> : null}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs line-clamp-2" title={f.name}>{f.name}</div>
                        <div className="mono text-[10px] text-slate-500 mt-0.5">
                          Ordered {f.times_ordered}× before
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="mono text-xs font-semibold">₹{Number(f.price).toLocaleString("en-IN")}</span>
                          {inCart ? (
                            <div className="inline-flex items-center gap-1">
                              <button onClick={() => remove(f.id)} className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="mono text-xs w-5 text-center">{inCart.quantity}</span>
                              <button onClick={() => add({ id: f.id, name: f.name, price: f.price, images: [f.image] }, 1)} className="w-6 h-6 rounded bg-[var(--shop-primary,#0055FF)] text-white hover:bg-[var(--shop-primary,#0040CC)] flex items-center justify-center">
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => add({ id: f.id, name: f.name, price: f.price, images: [f.image] }, 1)}
                              className="mono text-[10px] px-2 py-1 rounded bg-[#FF5000] hover:bg-[#e04600] text-white"
                              data-testid={`shop-fav-add-${f.id}`}
                            >
                              + Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-8 text-center text-slate-500 text-sm">
              {items.length === 0 ? "The shop is empty right now — check back soon." : "No products match your search."}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="shop-catalog-groups">
            {grouped.map(([groupName, groupItems]) => {
              const collapsed = collapsedGroups.has(groupName);
              return (
                <section key={groupName} data-testid={`shop-group-${groupName}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    className="w-full flex items-center justify-between mb-3 group hover:opacity-90"
                    data-testid={`shop-group-toggle-${groupName}`}
                    aria-expanded={!collapsed}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-lg font-bold text-slate-900">{groupName}</span>
                      <span className="mono text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                        {groupItems.length}
                      </span>
                    </div>
                    <span className="mono text-xs text-slate-400 group-hover:text-slate-700 transition">
                      {collapsed ? "Show +" : "Hide −"}
                    </span>
                  </button>
                  {!collapsed && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {groupItems.map((t) => {
                        const inCart = cart[t.id];
                        // Out-of-stock items still render — customer can browse them,
                        // but the Add-to-Cart is disabled and a red badge sits on the
                        // image so it's obvious at a glance.
                        const oos = (t.quantity || 0) <= 0;
                        return (
                          <Card key={t.id} className="border-slate-200 shadow-none overflow-hidden" data-testid={`shop-item-${t.id}`}>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!t.images?.[0]) return;
                      // Open instantly with whatever we already have (the
                      // sliced-first thumbnail), then swap in the full
                      // gallery once the detail endpoint responds. The
                      // shopper never sees a loading spinner.
                      setGalleryItem({ images: t.images, name: t.name });
                      try {
                        const params = ownerId ? { owner_id: ownerId } : {};
                        const { data } = await api.get(`/store/items/${t.id}`, { params });
                        if (data?.images?.length) {
                          setGalleryItem({ images: data.images, name: t.name });
                        }
                      } catch { /* keep the single-image gallery on failure */ }
                    }}
                    className="aspect-square w-full bg-slate-100 relative block focus:outline-none focus:ring-2 focus:ring-[var(--shop-primary,#0055FF)] group"
                    data-testid={`shop-item-image-${t.id}`}
                    aria-label={`View photos of ${t.name}`}
                  >
                    {t.images && t.images[0] ? (
                      <>
                        <img
                          src={t.images[0]}
                          alt={t.name}
                          loading="lazy"
                          decoding="async"
                          className={`w-full h-full object-cover group-hover:scale-[1.03] transition-transform ${oos ? "grayscale opacity-70" : ""}`}
                        />
                        {t.images.length > 1 && (
                          <div className="absolute bottom-1 right-1 text-[9px] bg-black/60 text-white rounded px-1.5 py-0.5 mono" data-testid={`shop-item-image-count-${t.id}`}>
                            +{t.images.length - 1}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center text-slate-300 text-xs mono ${oos ? "opacity-70" : ""}`}>no image</div>
                    )}
                    <div className="absolute top-1 left-1 text-[9px] bg-amber-50/95 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5 mono">
                      Reference
                    </div>
                    {oos && (
                      <div
                        className="absolute top-1 right-1 text-[9px] bg-red-600 text-white rounded px-1.5 py-0.5 mono font-semibold uppercase tracking-wider"
                        data-testid={`shop-item-oos-${t.id}`}
                      >
                        Out of stock
                      </div>
                    )}
                  </button>
                  <CardContent className="p-3">
                    <div className="text-sm font-medium line-clamp-2" title={t.name}>{t.name}</div>
                    <div className="mono text-[10px] text-slate-500 mt-0.5">{t.group || "\u00A0"}</div>
                    <div className="mt-1 mono font-semibold">₹{t.price?.toLocaleString("en-IN")}</div>
                    <div className={`mono text-[10px] ${oos ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                      {oos ? "Currently unavailable" : `In stock: ${t.quantity}`}
                    </div>
                    <div className="mt-2">
                      {oos ? (
                        <Button
                          size="sm"
                          disabled
                          className="w-full h-8 bg-slate-200 text-slate-500 cursor-not-allowed hover:bg-slate-200"
                          data-testid={`shop-add-oos-${t.id}`}
                        >
                          Out of stock
                        </Button>
                      ) : !inCart ? (
                        <Button
                          size="sm"
                          className="w-full h-8 bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)]"
                          onClick={() => add(t, 1)}
                          data-testid={`shop-add-${t.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => add(t, -1)} data-testid={`shop-dec-${t.id}`}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <div className="flex-1 text-center mono text-sm" data-testid={`shop-qty-${t.id}`}>{inCart.quantity}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            onClick={() => add(t, 1)}
                            disabled={inCart.quantity >= (t.quantity || 0)}
                            data-testid={`shop-inc-${t.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* Downloads list — opened from the "Download" button in the header. */}
      <Dialog open={downloadsOpen} onOpenChange={setDownloadsOpen}>
        <DialogContent className="max-w-md" data-testid="shop-downloads-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-4 h-4" /> Downloads
            </DialogTitle>
          </DialogHeader>
          <div className="border border-slate-200 rounded-md divide-y max-h-[60vh] overflow-y-auto">
            {downloads.map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`shop-download-${d.id}`}>
                <FileDown className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.title}</div>
                  {d.description && (
                    <div className="text-xs text-slate-500 truncate">{d.description}</div>
                  )}
                  <div className="mono text-[10px] text-slate-400 mt-0.5">
                    {d.file_name} · {humanBytes(d.file_size)}
                  </div>
                </div>
                <a
                  href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/store/downloads/${d.id}/file${ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : ""}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-white hover:opacity-90 shrink-0"
                  style={{ backgroundColor: brandColor }}
                  data-testid={`shop-download-link-${d.id}`}
                >
                  <FileDown className="w-3.5 h-3.5" /> Download
                </a>
              </div>
            ))}
            {downloads.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">No files yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {galleryItem && (
        <ProductGallery
          images={galleryItem.images}
          name={galleryItem.name}
          onClose={() => setGalleryItem(null)}
        />
      )}

      <ShopFooter />

      {/* PWA install nudge — appears once per session on installable
          browsers with a friendly shop-branded pill (iOS gets a modal
          walkthrough since Safari won't do it natively). */}
      <InstallPrompt shopName={tenantBrand?.shop_name || ""} />
    </div>
  );
}
